// MLS KeyPackage + device fetchers, used by the chat-invite flow to
// add a peer to a freshly minted MLS conversation before the first
// application message is sent.
//
// Canonical endpoint (openapi.yaml line 462+):
//
//   GET /v1/devices/{user_id}
//     → { devices: [{ device_id, key_package, last_seen_at }, ...] }
//
// `key_package` is base64url-encoded MLS bytes — atomic resolve of
// device + KP in one round-trip. The older two-call pattern
// (`GET /v1/users/{id}/devices` + `GET /v1/users/{id}/key-package`)
// is retired on the canonical backend; the kept helpers below are
// thin wrappers that map the new shape into the names downstream
// callers already know.
//
// These endpoints are public-by-design per RFC 9420 — any
// authenticated caller can list devices and read KeyPackages from
// any user's pool.

import type { ApiClient } from '../transport-mls/PingTransport';

export interface PeerDevice {
  readonly device_id: string;
  /** No longer surfaced by the canonical endpoint; kept for type
   *  parity with legacy callers. */
  readonly signature_public_key_ref: string;
}

export interface PeerKeyPackage {
  readonly keyPackageRefHex: string;
  readonly packageBytes: Uint8Array;
  readonly poolSizeAfter: number;
}

interface DeviceDiscoverResponseWire {
  readonly devices: ReadonlyArray<{
    readonly device_id: string;
    readonly key_package: string;
    readonly last_seen_at: number;
  }>;
}

// Module-level cache from `(userId, deviceId)` → key_package bytes,
// populated by `fetchPeerDevices` so a subsequent
// `consumePeerKeyPackage(userId, deviceId)` in the same call chain
// doesn't have to re-fetch. The canonical endpoint returns both
// device + KP atomically, but the two-step helper signature is kept
// for backwards compatibility with callers that haven't migrated.
// Entries are evicted after consume (single-use semantics).
const PEER_KP_CACHE = new Map<string, Uint8Array>();

function cacheKey(userId: string, deviceId: string): string {
  return `${userId}::${deviceId}`;
}

export async function fetchPeerDevices(
  api: ApiClient,
  userId: string,
): Promise<readonly PeerDevice[]> {
  const resp = await api.get<DeviceDiscoverResponseWire>(
    `/v1/devices/${encodeURIComponent(userId)}`,
  );
  const devices = resp.devices ?? [];
  // Pre-populate the per-device KP cache so the matched-pair
  // `consumePeerKeyPackage` call below doesn't need a second
  // network hit. The canonical endpoint hands us both in one shot.
  for (const d of devices) {
    PEER_KP_CACHE.set(cacheKey(userId, d.device_id), base64urlToBytes(d.key_package));
  }
  return devices.map((d) => ({
    device_id: d.device_id,
    signature_public_key_ref: '',
  }));
}

export async function consumePeerKeyPackage(
  api: ApiClient,
  params: {
    userId: string;
    deviceId?: string;
  },
): Promise<PeerKeyPackage> {
  // Prefer the cache populated by `fetchPeerDevices`. Cache miss
  // (e.g. caller skipped the device-listing step) falls back to a
  // fresh fetch.
  if (params.deviceId) {
    const cached = PEER_KP_CACHE.get(cacheKey(params.userId, params.deviceId));
    if (cached) {
      PEER_KP_CACHE.delete(cacheKey(params.userId, params.deviceId));
      return {
        // The canonical endpoint doesn't expose a separate
        // `key_package_ref` — the bytes themselves are the
        // identity; use the device id as a stable handle.
        keyPackageRefHex: params.deviceId,
        packageBytes: cached,
        poolSizeAfter: 0,
      };
    }
  }
  const resp = await api.get<DeviceDiscoverResponseWire>(
    `/v1/devices/${encodeURIComponent(params.userId)}`,
  );
  const match = params.deviceId
    ? resp.devices.find((d) => d.device_id === params.deviceId)
    : resp.devices[0];
  if (!match) {
    throw new Error('peer device not found');
  }
  return {
    keyPackageRefHex: match.device_id,
    packageBytes: base64urlToBytes(match.key_package),
    poolSizeAfter: 0,
  };
}

function base64urlToBytes(s: string): Uint8Array {
  // Canonical BE encodes via `base64.RawURLEncoding` (alphabet `-_`,
  // no padding). Restore standard alphabet + padding before `atob`.
  // Tolerant of standard-base64 inputs too (`+/=` chars) so the same
  // helper works if a caller hands us either variant.
  const padding = '='.repeat((4 - (s.length % 4)) % 4);
  const standard = (s + padding).replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(standard);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
